import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import { Drawer, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { Box, Button, Grid, Menu, MenuItem, Stack } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import InsightsIcon from '@mui/icons-material/Insights';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';

import logo from 'src/App/assets/logoMobile.jpg';
import logoMini from 'src/App/assets/logoMobileSidebarCollapsed.jpg';
import { SidebarProps } from 'src/interfaces/iproptypes';
import { SidebarItemChildrenType, SidebarItemType } from 'src/interfaces/idashboard';
import useStyles from './styles';
import SidebarItem from './SidebarItem';
import CurrencyChangeMenu from '../CurrencyChangeMenu';
import { useOpenOverviewOnboardingPage } from 'src/utils/hooks';
import { COLOR_PRIMARY, sidebarClosedWidth, sidebarWidth } from 'src/utils/const';
import { COLOR_GREY_600, COLOR_PRIMARY_2 } from 'src/utils/colors';
import { useHistory } from 'react-router-dom';
import { openSidebarRedirectionLink } from './sidebarLinkUtils';
import { RoutesMap, HOME_CHAT_SIDEBAR_LIST_INDEX } from 'src/utils/const';
const sideBarIconStyle = { fontSize: 25, color: '#000', padding: '2 0' };

// main component

const sidebarItems: SidebarItemType[] = [
  {
    icon: <HomeRoundedIcon style={sideBarIconStyle} />,
    name: 'Home',
    isPage: false,
    stateName: 'home',
    listIndex: HOME_CHAT_SIDEBAR_LIST_INDEX,
    children: [],
  },
  {
    icon: <InsightsIcon style={sideBarIconStyle} />,
    name: 'Overview',
    isPage: false,
    stateName: 'main',
    listIndex: 0,
    children: [],
  },
  {
    icon: <CalculateIcon style={sideBarIconStyle} />,
    name: 'Calculator',
    isPage: false,
    stateName: 'estimator',
    listIndex: 1,
    children: [],
  },
  {
    icon: <TravelExploreIcon style={sideBarIconStyle} />,
    name: 'Explorer',
    isPage: false,
    stateName: 'compare',
    listIndex: 2,
    children: [],
  },
  {
    icon: <LayersOutlinedIcon style={sideBarIconStyle} />,
    name: 'Advanced Solutions',
    isPage: false,
    stateName: '',
    listIndex: 13,
    children: [
      {
        name: 'API',
        isPage: true,
        stateName: '',
        listIndex: 14,
        redirectionLink: RoutesMap.ApiDashboard,
        tooltip: 'API dashboard — usage, keys, and endpoints',
      },
      {
        name: 'Property-level Performance Data',
        isPage: true,
        stateName: '',
        listIndex: 15,
        redirectionLink: 'https://airbtics.com/airbnb-dataset',
        tooltip: 'CSV dataset of Airbnb listing performance',
      },
      {
        name: 'Hotel Data',
        isPage: true,
        stateName: '',
        listIndex: 17,
        redirectionLink: 'https://airbtics.com/hotel-data',
        tooltip: 'Hotel market data: occupancy, demand, OTAs, CSV/API',
      },
      {
        name: 'Enterprise',
        isPage: true,
        stateName: '',
        listIndex: 16,
        redirectionLink: 'https://airbtics.com/airbnb-data',
        tooltip: 'Enterprise and custom data solutions',
      },
    ],
  },
];

const Sidebar = ({
  parent,
  handleSidebarClick,
  sidebarOpen,
  setSidebarOpen,
  matches,
  selectedSidebarIndex,
  handleSidebarListItemClick,
  currency,
  setCurrency,
  isUserVerified,
  setIsEmailVerifyModalOpen,
  dashboardState,
  user,
  isLoggedIn,
  setOpenReportComparePage,
}: SidebarProps) => {
  const classes = useStyles();
  const [sideBarReadyToCollapse, setSideBarReadyToCollapse] = useState(false);
  const [mobileGroupMenu, setMobileGroupMenu] = useState<{
    anchor: HTMLElement | null;
    children: SidebarItemChildrenType[] | null;
  }>({ anchor: null, children: null });

  const history = useHistory();

  useEffect(() => {
    if (window.innerWidth < 1050) {
      setSidebarOpen(false);
    }
  }, [matches]);

  useEffect(() => {
    setTimeout(() => {
      setSideBarReadyToCollapse(sidebarOpen);
    }, 500);
  }, [sidebarOpen]);

  const isTopBannerOpen = useSelector((state: any) => state.dashboard.isTopBannerOpen);
  const openOverviewOnboardingPage = useOpenOverviewOnboardingPage();

  const handleClickAway = () => {
    if (matches && sideBarReadyToCollapse) {
      setSidebarOpen(false);
    }
  };

  const EmailVerificationComponent = (
    <ListItem
      style={{
        cursor: 'pointer',
        pointerEvents: 'auto',
        paddingLeft: '0',
        ...(matches && { display: 'flex', justifyContent: 'center', alignItems: 'center' }),
      }}
      onClick={() => setIsEmailVerifyModalOpen(true)}
    >
      <ListItemIcon>
        <Stack
          direction={'column'}
          justifyContent="flex-start"
          alignItems="center"
          spacing={0}
          sx={{
            background: 'transparent',
            padding: '2.5px 4px',
            paddingRight: '12px',
            borderRadius: 4,
            maxWidth: matches ? sidebarWidth : sidebarClosedWidth,
          }}
        >
          <MarkEmailUnreadIcon
            style={{
              color: '#000',
              animation: 'shake-up-down 3.5s 5s ease-in-out infinite',
              paddingRight: '5px',
            }}
          />
          <ListItemText
            classes={{
              primary: classes.sidebarText,
            }}
            primary="Verify Email"
            style={{ maxWidth: sidebarClosedWidth }}
          />
        </Stack>
      </ListItemIcon>
    </ListItem>
  );

  const isEmailVerificationOptionVisible = isLoggedIn && isUserVerified === false && !user.loading;

  // Mobile bottom bar
  if (matches) {
    return (
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          bottom: 0,
          right: 0,
          background: '#fff',
          boxShadow: '0 -4px 12px 0px #0003',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          zIndex: 1200,
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          padding: '8px 0',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            padding: '0 16px',
            minWidth: 'max-content',
            justifyContent:
              sidebarItems.filter(
                item => !(user.subscriptionPlan == 'starter' && item.listIndex === 3)
              ).length <= 3
                ? 'center'
                : 'flex-start',
            gap: '20px',
          }}
        >
          {sidebarItems.map(sideBarItem => {
            if (user.subscriptionPlan == 'starter' && sideBarItem.listIndex === 3) {
              return null;
            }
            const hasChildLinks = (sideBarItem.children?.length ?? 0) > 0;
            return (
              <Box key={sideBarItem.listIndex} sx={{ position: 'relative', flexShrink: 0 }}>
                <Button
                  onClick={e => {
                    setOpenReportComparePage(false);
                    if (hasChildLinks) {
                      setMobileGroupMenu({
                        anchor: e.currentTarget,
                        children: sideBarItem.children as SidebarItemChildrenType[],
                      });
                      return;
                    }
                    if (sideBarItem.name === 'Calculator') {
                      history.push('/airbnb-calculator');
                    }
                    handleSidebarListItemClick(sideBarItem.listIndex);
                    handleSidebarClick && handleSidebarClick(sideBarItem.stateName);
                  }}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff',
                    color:
                      sideBarItem.listIndex === selectedSidebarIndex
                        ? COLOR_PRIMARY_2
                        : COLOR_GREY_600,
                    transform: 'scale(0.85)',
                    padding: '8px 12px',
                    minWidth: '80px',
                    '& .MuiSvgIcon-root': {
                      color:
                        (sideBarItem.listIndex === selectedSidebarIndex
                          ? COLOR_PRIMARY_2
                          : COLOR_GREY_600) + '!important',
                    },
                    '& .MuiButton-startIcon': {
                      marginLeft: 0,
                      marginRight: 0,
                    },
                  }}
                  startIcon={sideBarItem.icon}
                >
                  {hasChildLinks ? 'Advanced' : sideBarItem.name}
                </Button>
                {sideBarItem.isBeta && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '0px',
                      right: '0px',
                      padding: '2px 5px',
                      fontFamily: 'roboto',
                      borderRadius: '200px',
                      border: '1px solid ' + COLOR_PRIMARY_2,
                      fontSize: '10px',
                      zIndex: 10,
                      background: 'white',
                      color: COLOR_PRIMARY_2,
                    }}
                  >
                    Beta
                  </Box>
                )}
              </Box>
            );
          })}

          <Menu
            anchorEl={mobileGroupMenu.anchor}
            open={Boolean(mobileGroupMenu.anchor)}
            onClose={() => setMobileGroupMenu({ anchor: null, children: null })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            {mobileGroupMenu.children?.map(child => (
              <MenuItem
                key={child.listIndex}
                onClick={() => {
                  openSidebarRedirectionLink(child.redirectionLink, history);
                  setMobileGroupMenu({ anchor: null, children: null });
                }}
              >
                {child.name}
              </MenuItem>
            ))}
          </Menu>

          {isEmailVerificationOptionVisible && (
            <Box sx={{ flexShrink: 0, minWidth: '80px' }}>{EmailVerificationComponent}</Box>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(classes.paperDrawer, {
            [classes.drawerOpen]: sidebarOpen && matches,
            [classes.drawerClose]: !sidebarOpen || (sidebarOpen && !matches),
            [classes.bannerOpen]: isTopBannerOpen,
          }),
        }}
        className="sidebar-scroll"
      >
        <Button
          className={classes.logoButton}
          onClick={() => {
            openOverviewOnboardingPage();
          }}
        >
          {matches && sidebarOpen ? (
            <img src={logo} className={classes.logo} />
          ) : (
            <img src={logoMini} className={classes.logoMini} />
          )}
        </Button>

        {/* Dashboard */}
        <div>
          {sidebarItems.map((data, index) => {
            if (user.subscriptionPlan == 'starter' && data.listIndex === 3) {
              return null;
            }
            return (
              <SidebarItem
                key={`sidebar-item-${index}`}
                item={data}
                isSubGroup={false}
                handleSidebarClick={handleSidebarClick}
                handleSidebarListItemClick={handleSidebarListItemClick}
                matches={matches}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                index={index}
                selectedSidebarIndex={selectedSidebarIndex}
                user={user}
                setOpenReportComparePage={setOpenReportComparePage}
              />
            );
          })}

          {/* Email verification button */}
          {isEmailVerificationOptionVisible && (
            <div
              style={{
                backgroundColor: 'transparent',
                margin: '10px',
                borderRadius: 10,
                overflow: 'hidden',
              }}
            >
              {EmailVerificationComponent}
            </div>
          )}
        </div>

        {matches && currency && (
          <List
            disablePadding={true}
            style={{
              position: 'relative',
              cursor: 'pointer',
              width: '100%',
              marginBottom: '20px',
            }}
          >
            <ListItem style={{ paddingLeft: 0 }}>
              <CurrencyChangeMenu
                currency={currency}
                setCurrency={setCurrency}
                fullWidth={true}
                darkTheme={true}
              />
            </ListItem>
          </List>
        )}
      </Drawer>
    </ClickAwayListener>
  );
};

export default Sidebar;
